import React, { useState, useRef, useContext, useEffect, useCallback } from 'react';
import { CCol, CButton, CFormSelect, CFormTextarea, CCard, CCardBody, CCardHeader, CNav, CNavItem, CNavLink, CTabContent, CTabPane, CButtonGroup } from '@coreui/react';
import { AuthUserContext } from '../session';
import axios from 'axios';
import config from '../../utils/EnvironmentSwitcher';
import { LiveAvatarComponent } from './AIAvatar/LiveAvatarComponent';
import { getFhirToken } from '../../utils/fhirAuth';
import { callAI } from '../../services/aiChatService';

/**
 * GptMiEWidget - GPT chat + HeyGen LiveAvatar
 *
 * Avatar tab uses LiveAvatarComponent (livekit + LiveAvatar API).
 * Legacy @heygen/streaming-avatar was removed — npm publishes that package without lib/.
 */
const fhirEndpoint = config.fhirUrl;

// Utility function to encode text to Base64
const toBase64 = (str) => {
      return window.btoa(unescape(encodeURIComponent(str)));
};

const GptMiEWidget = () => {
      const authUser = useContext(AuthUserContext);
      const [isListening, setIsListening] = useState(false);
      const [text, setText] = useState('');
      const [gptResponse, setGptResponse] = useState(''); // Store GPT response
      const recognitionRef = useRef(null);
      const finalTranscriptRef = useRef(''); // Store the final transcript separately to prevent state issues
      const synthRef = useRef(window.speechSynthesis); // Reference to speech synthesis
      const [isSpeaking, setIsSpeaking] = useState(false); // State to manage speaking status
      const [voices, setVoices] = useState([]); // Available voices
      const [selectedVoice, setSelectedVoice] = useState(null); // Selected voice
      const isPlayingRef = useRef(false); // Ref to track if speech is playing
      const browserSupportsSpeechRecognition =
            'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
      const [isTyping, setIsTyping] = useState(true);
      const [isThinking, setIsThinking] = useState(false);
      
      // LiveAvatar API key (sandbox-friendly by default)
      const [liveAvatarKey] = useState(
            import.meta.env.VITE_LIVEAVATAR_API_KEY || import.meta.env.VITE_HEYGEN_API_KEY || ''
      );
      const [useSandboxMode] = useState(true);
      
      // Tab management state
      const [activeTab, setActiveTab] = useState('interactive-avatar');
      
      
      // Window size state - starts at half size
      const [isExpanded, setIsExpanded] = useState(false);
      
      // Interactive Avatar error handling
      const handleAvatarError = (error) => {
            console.error('Interactive Avatar Error:', error);
            alert(`Avatar Error: ${error.message || 'An unexpected error occurred'}`);
      };

      useEffect(() => {
            const loadVoices = () => {
                  const availableVoices = synthRef.current.getVoices();
                  setVoices(availableVoices);

                  const defaultVoice = availableVoices.find(
                        (voice) =>
                              voice.name.toLowerCase().includes('female') ||
                              voice.name.toLowerCase().includes('google uk english')
                  );
                  setSelectedVoice(defaultVoice || availableVoices[0]);
            };

            if (synthRef.current.onvoiceschanged !== undefined) {
                  synthRef.current.onvoiceschanged = loadVoices;
            }

            loadVoices();
      }, []);



      // Cleanup avatar session on component unmount
      useEffect(() => {
            return () => {
                  // Clean up any active avatar session on unmount
                  console.log("Cleaning up on component unmount");
                  // Note: stopAvatarSession will handle checking if there's an active session
            };
      }, []);

      const exportToFHIR = async (authUser) => {
            const currentDateTime = new Date().toISOString();
            const encodedTranscribedText = toBase64(text);
            try {
                  const token = await getFhirToken(false);
                  if (!token) throw new Error('Missing FHIR token');
                  const docItem = {
                        resourceType: 'DocumentReference',
                        status: 'current',
                        identifier: [
                              {
                                    system: 'https://fhir.mieros.cloud/fhir/ids',
                                    value: authUser.authUser.uid,
                              },
                        ],
                        type: {
                              coding: [
                                    {
                                          system: 'http://loinc.org',
                                          code: '11502-2',
                                          display: 'General Note',
                                    },
                              ],
                        },
                        created: currentDateTime,
                        content: [
                              {
                                    attachment: {
                                          contentType: 'text/plain',
                                          data: encodedTranscribedText,
                                          title: 'Transcribed Notes: mieros.cloud',
                                          creation: currentDateTime,
                                    },
                              },
                        ],
                        context: {
                              period: {
                                    start: currentDateTime,
                                    end: currentDateTime,
                              },
                        },
                  };

                  await axios.post(`${fhirEndpoint}/DocumentReference`, docItem, {
                        headers: {
                              'Content-Type': 'application/fhir+json',
                              'Authorization': `Bearer ${token}`,
                        },
                  });

                  alert('Data exported to FHIR successfully!');
            } catch (error) {
                  console.error('Error exporting data to FHIR:', error);
                  alert('Failed to export data to FHIR.');
            }
      };



      const startListening = () => {
            if (!browserSupportsSpeechRecognition) {
                  alert('Your browser does not support speech recognition.');
                  return;
            }

            setText('');
            finalTranscriptRef.current = ''; // Reset the final transcript reference

            const SpeechRecognition =
                  window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();

            recognition.continuous = true;
            recognition.interimResults = true;

            recognition.onstart = () => {
                  setIsListening(true);
            };

            recognition.onresult = (event) => {
                  let interimTranscript = '';
                  for (let i = event.resultIndex; i < event.results.length; i++) {
                        const transcript = event.results[i][0].transcript;
                        if (event.results[i].isFinal) {
                              finalTranscriptRef.current += ' ' + transcript;
                              setText((prevText) => prevText + ' ' + transcript);
                        } else {
                              interimTranscript += transcript;
                        }
                  }
            };

            recognition.onerror = (event) => {
                  console.error('Speech recognition error:', event.error);
                  setIsListening(false);
            };

            recognition.onend = () => {
                  setIsListening(false);
                  console.log('Here is the text: ', finalTranscriptRef.current); // Log the final transcript
                  sendToGpt(finalTranscriptRef.current); // Send the final transcribed text to GPT API
            };

            recognition.start();
            recognitionRef.current = recognition;
      };

      const stopListening = () => {
            if (recognitionRef.current) {
                  recognitionRef.current.stop();
            }
      };

      const sendToGpt = async (transcription) => {
            try {
                  setIsThinking(true); // Start thinking animation
                  setGptResponse(''); // Clear previous response while waiting

                  // All prompts go to the mieGPT endpoint (https://chat.mieros.cloud/gptj).
                  const responseText = await callAI({ prompt: transcription });

                  setGptResponse(responseText);
            } catch (error) {
                  console.error('Error communicating with GPT API:', error);
                  const errorMessage = error.response?.data?.error?.message || error.message || 'Unknown error';
                  setGptResponse(`Error: Unable to fetch response from mieGPT. ${errorMessage}`);
            } finally {
                  setIsThinking(false); // Stop thinking animation
            }
      };

      const playResponse = useCallback(() => {
            if (!gptResponse) {
                  console.log("No GPT response to play");
                  return;
            }

            // Traditional text-to-speech only
            if (synthRef.current.speaking) {
                  console.log("Cancelling ongoing speech");
                  synthRef.current.cancel();
                  setIsSpeaking(false);
                  isPlayingRef.current = false;
                  return;
            }

            const utterance = new SpeechSynthesisUtterance(gptResponse);
            if (selectedVoice) {
                  utterance.voice = selectedVoice;
                  console.log("Playing response with selected voice:", selectedVoice.name);
            } else {
                  console.log("Selected voice not set, using default");
            }

            utterance.onstart = () => {
                  console.log("Speech started");
                  setIsSpeaking(true);
                  isPlayingRef.current = true; // Set ref to true when speech starts
            };
            utterance.onend = () => {
                  console.log("Speech ended");
                  setIsSpeaking(false);
                  isPlayingRef.current = false; // Reset ref when speech ends
            };
            utterance.onerror = (event) => {
                  console.error("Speech synthesis error:", event.error);
                  setIsSpeaking(false);
                  isPlayingRef.current = false; // Reset ref on error
            };

            synthRef.current.speak(utterance);
      }, [gptResponse, selectedVoice]);

      // Auto-play GPT response when it's available
      useEffect(() => {
            if (gptResponse && !isPlayingRef.current) {
                  console.log("Attempting to play response");
                  playResponse(); // Use traditional text-to-speech
            }
      }, [gptResponse, playResponse]);

      const handleVoiceChange = (event) => {
            const voiceName = event.target.value;
            const newVoice = voices.find((voice) => voice.name === voiceName);
            setSelectedVoice(newVoice);
      };

      // Add function to handle typing mode
      const startTyping = () => {
            setIsTyping(true);
            setText(''); // Clear existing text
      };

      return (
            <div
                  style={{
                        padding: '8px',
                        fontSize: '14px',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        color: 'var(--text-primary)',
                        width: '100%',
                        height: isExpanded ? '100%' : '50%',
                        minHeight: isExpanded ? '600px' : '300px',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'transparent',
                        transition: 'height 0.3s ease, min-height 0.3s ease',
                  }}
            >
                  <CCard style={{ height: '100%', display: 'flex', flexDirection: 'column', color: 'var(--text-primary)' }}>
                        <CCardHeader style={{ padding: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'var(--text-primary)', fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
                              <CNav variant="tabs" role="tablist" style={{ flex: 1 }}>
                                    <CNavItem>
                                          <CNavLink
                                                active={activeTab === 'interactive-avatar'}
                                                onClick={() => setActiveTab('interactive-avatar')}
                                                style={{ cursor: 'pointer', fontSize: '14px', padding: '0.5rem 1rem' }}
                                          >
                                                Interactive Avatar
                                          </CNavLink>
                                    </CNavItem>
                                    <CNavItem>
                                          <CNavLink
                                                active={activeTab === 'gpt-chat'}
                                                onClick={() => setActiveTab('gpt-chat')}
                                                style={{ cursor: 'pointer', fontSize: '14px', padding: '0.5rem 1rem' }}
                                          >
                                                GPT Chat
                                          </CNavLink>
                                    </CNavItem>
                              </CNav>
                              
                              {/* Expand/Collapse Button */}
                              <CButton
                                    size="sm"
                                    color="outline-secondary"
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    style={{ 
                                          marginLeft: '10px',
                                          fontSize: '12px',
                                          padding: '0.25rem 0.5rem'
                                    }}
                              >
                                    {isExpanded ? '🔽 Collapse' : '🔼 Expand'}
                              </CButton>
                        </CCardHeader>
                        <CCardBody style={{ flex: 1, padding: '0.5rem', overflow: 'hidden' }}>
                              <CTabContent>
                                    <CTabPane role="tabpanel" visible={activeTab === 'interactive-avatar'}>
                                          <div style={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column' 
                                          }}>
                                                {/* LiveAvatar — streaming-avatar npm package is broken (no lib/) */}
                                                <LiveAvatarComponent
                                                      apiKey={liveAvatarKey}
                                                      useSandbox={useSandboxMode}
                                                      onError={handleAvatarError}
                                                      customStyles={{
                                                            containerStyle: {
                                                                  height: '100%',
                                                                  fontSize: '14px'
                                                            },
                                                            cardStyle: {
                                                                  height: '100%',
                                                                  borderRadius: '8px',
                                                                  overflow: 'hidden'
                                                            }
                                                      }}
                                                />
                                          </div>
                                    </CTabPane>
                                    
                                    <CTabPane role="tabpanel" visible={activeTab === 'gpt-chat'}>
                                          <div style={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                fontSize: '12px'
                                          }}>
                                                <div style={{ marginBottom: '10px', color: 'var(--text-secondary)' }}>GPT MiE - Text Chat with Voice (Use Interactive Avatar tab for full avatar experience)</div>
                                                
                                                <CCol sm="12" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '5px', flexWrap: 'wrap' }}>
                              <CButton
                                    onClick={isListening ? stopListening : startListening}
                                    size="sm"
                                    color="primary"
                              >
                                    {isListening ? 'Stop Listening' : 'Start Listening'}
                              </CButton>
                              <CButton
                                    onClick={startTyping}
                                    size="sm"
                                    color="success"
                              >
                                    Type Message
                              </CButton>
                        </div>
                        
                        {/* Show either text display or input field */}
                        {isTyping ? (
                              <div style={{ marginBottom: '10px' }}>
                                    <CFormTextarea
                                          value={text}
                                          onChange={(e) => setText(e.target.value)}
                                          rows={3}
                                          placeholder="Type your message here..."
                                          style={{ fontSize: '12px' }}
                                    />
                                    <div style={{ marginTop: '5px', display: 'flex', gap: '5px' }}>
                                          <CButton
                                                size="sm"
                                                color="primary"
                                                onClick={() => {
                                                      sendToGpt(text);
                                                      setIsTyping(false);
                                                }}
                                          >
                                                Send
                                          </CButton>
                                          <CButton
                                                size="sm"
                                                color="danger"
                                                onClick={() => {
                                                      setText('');
                                                      setIsTyping(false);
                                                }}
                                          >
                                                Cancel
                                          </CButton>
                                    </div>
                              </div>
                        ) : (
                              <p style={{ fontSize: '12px', margin: '5px 0', flex: '1' }}>
                                    <strong>Transcribed Text:</strong> {text}
                              </p>
                        )}
                  </CCol>
                  <div
                        style={{
                              padding: '5px',
                              border: '1px solid var(--border-default)',
                              borderRadius: '5px',
                              marginTop: '5px',
                              overflowY: 'auto',
                                                            maxHeight: '200px',
                              flex: '1',
                        }}
                  >
                        <strong>GPT Response:</strong>
                        {isThinking ? (
                              <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px',
                                    padding: '10px',
                                    color: 'var(--text-muted)'
                              }}>
                                                                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                                  <div>Thinking with mieGPT...</div>
                              </div>
                        ) : (
                              <p style={{ 
                                                                  fontSize: '12px', 
                                    margin: '5px 0' 
                              }}>
                                    {gptResponse}
                              </p>
                        )}
                  </div>
                  <div
                        style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginTop: '5px',
                                                            gap: '5px'
                        }}
                  >
                        <CButton
                              color="primary"
                              size="sm"
                              onClick={playResponse}
                              style={{ fontSize: '10px', padding: '2px 6px' }}
                        >
                              {isSpeaking ? 'Stop Playback' : 'Play Response'}
                        </CButton>
                        <CButton
                              color="success"
                              size="sm"
                              onClick={() => exportToFHIR(authUser)}
                              style={{ fontSize: '10px', padding: '2px 6px' }}
                        >
                              Export to FHIR
                        </CButton>
                  </div>
                                                <div style={{ marginTop: '5px' }}>
                        <CFormSelect
                              size="sm"
                              value={selectedVoice ? selectedVoice.name : ''}
                              onChange={handleVoiceChange}
                                                            style={{ fontSize: '10px' }}
                        >
                              {voices.map((voice, index) => (
                                    <option key={index} value={voice.name}>
                                          {voice.name} ({voice.lang})
                                    </option>
                              ))}
                        </CFormSelect>
                  </div>
                                                
                                                {/* Status indicator */}
                        <div className="small mt-2" style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                                                      Using: mieGPT
                                                </div>
                                          </div>
                                    </CTabPane>
                              </CTabContent>
                        </CCardBody>
                  </CCard>
            </div>
      );
};



export default GptMiEWidget;


